/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton} from '@mui/material';
import ArgosFlexbox from "../../components/Containers/ArgosFlexbox";
import Tooltip from "@mui/material/Tooltip";
import {ArgosEditIcon} from "../../components/Icons/ArgosActionIcons";
import {LegacyRef} from 'react';
import ArgosTextField from '../../components/Form/TextField/ArgosTextField';
import ArgosButton from '../../components/Buttons/ArgosButton';
import {validateURL} from "../../../helpers/url/urlHelpers";

/**
 * Renders the Object ID input field.
 * @param objectId - The ID of the object.
 * @param inputStyle - The style to be applied to the input field.
 * @returns A JSX element representing the Object ID input field.
 * @description This function creates a disabled input field that displays the Object ID.
 * The field is styled using the provided inputStyle. The label "Object ID" is displayed above the input.
 */
export const renderObjectId = ({ objectId, inputStyle }: { objectId: string | null, inputStyle: React.CSSProperties }) => {
  return (
    <div style={{ flex: 1, maxWidth: '325px', marginLeft: 'auto', alignSelf: 'flex-start'}}>
      <label htmlFor="objectId" style={{ display: 'block', marginBottom: '5px', color: '#0173CE' }}>
        Object ID
      </label>
      <input
        id="objectId"
        type="text"
        disabled={true}
        value={objectId ?? ""}
        style={inputStyle}
      />
    </div>
  );
};
/**
 * @description This function renders a button that opens a dialog for updating the URL. 
 * @param url - The URL to be updated.
 * @param handleUpdate  - The function to call when the URL is updated.
 * @param dialogOpen - A boolean indicating whether the dialog is open or not. 
 * @param setDialogOpen  - A function to set the dialog open state.
 * @param urlRef - A reference to the URL input field.
 * @param error - A boolean indicating whether there is an error with the URL input.
 * @param setError - A function to set the error state for the URL input. 
 * @returns A JSX element representing the update URL dialog and button.
 */
export const renderUpdateURL = (url: string, handleUpdate: () => void, dialogOpen: boolean, setDialogOpen: (open: boolean) => void, urlRef: LegacyRef<HTMLInputElement> | undefined, error: boolean, setError: (hasError: boolean) => void) => {

  return (
    <ArgosFlexbox style={{ marginTop: 20 }}>
      <Tooltip title='Update URL' placement='top' arrow>
        <IconButton
          style={{ width: 60, height: 60 }}
          onClick={() => setDialogOpen(true)}
        >
          <ArgosEditIcon style={{ width: 30, height: 30 }} />
        </IconButton>
      </Tooltip>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>Update URL</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter updated URL.
            </DialogContentText>
            <ArgosTextField
              style={{marginTop: 20}}
              fullWidth
              focused={true}
              margin="dense"
              onBlur={(e: any) => setError(e.target.value.trim() ? !validateURL(e.target.value) : false)}
              error={error}
              helperText={error && `Invalid URL`}

              inputRef={urlRef as React.Ref<any>}
              defaultValue={url ?? ""}

              type="text"
              label="URL"
            />
          </DialogContent>
          {!error && <DialogActions>
            <ArgosButton onClick={() => setDialogOpen(false)} color="secondary">Cancel</ArgosButton>
            <ArgosButton onClick={() => { handleUpdate(); setDialogOpen(false); }} color="primary">Save</ArgosButton>
          </DialogActions>}
        </Dialog>
    </ArgosFlexbox>

  );
};
